import alexAvatarUrl from "@/assets/images/zarvis/alex.jpg";
import bkAvatarUrl from "@/assets/images/zarvis/bk.jpg";
import dougAvatarUrl from "@/assets/images/zarvis/doug.png";
import kushalAvatarUrl from "@/assets/images/zarvis/kushal.jpg";
import namrataAvatarUrl from "@/assets/images/zarvis/namrata.jpg";
import nishaAvatarUrl from "@/assets/images/zarvis/nisha.png";
import sumanAvatarUrl from "@/assets/images/zarvis/suman.jpg";
import williamAvatarUrl from "@/assets/images/zarvis/william.png";
import workspaceIdentities from "../../../../config/zarvis_workspace_identities.json";

export const ZARVIS_ASSISTANT_NAMES = [
  "Alex",
  "BK",
  "Kushal",
  "Namrata",
  "Suman",
  "William",
  "Nisha",
  "Doug",
] as const;

export type ZarvisAssistantName = (typeof ZARVIS_ASSISTANT_NAMES)[number];
export type ZarvisAssistantKey = Lowercase<ZarvisAssistantName>;

export interface ZarvisIdentity {
  readonly displayName: ZarvisAssistantName;
  readonly avatarUrl: string;
}

const ALEX_IDENTITY = { displayName: "Alex", avatarUrl: alexAvatarUrl } as const satisfies ZarvisIdentity;
const BK_IDENTITY = { displayName: "BK", avatarUrl: bkAvatarUrl } as const satisfies ZarvisIdentity;
const KUSHAL_IDENTITY = { displayName: "Kushal", avatarUrl: kushalAvatarUrl } as const satisfies ZarvisIdentity;
const NAMRATA_IDENTITY = { displayName: "Namrata", avatarUrl: namrataAvatarUrl } as const satisfies ZarvisIdentity;
const SUMAN_IDENTITY = { displayName: "Suman", avatarUrl: sumanAvatarUrl } as const satisfies ZarvisIdentity;
const WILLIAM_IDENTITY = { displayName: "William", avatarUrl: williamAvatarUrl } as const satisfies ZarvisIdentity;
const NISHA_IDENTITY = { displayName: "Nisha", avatarUrl: nishaAvatarUrl } as const satisfies ZarvisIdentity;
const DOUG_IDENTITY = { displayName: "Doug", avatarUrl: dougAvatarUrl } as const satisfies ZarvisIdentity;

// Retain previous leaders alongside the current workspace assignments.
const IDENTITY_BY_ASSISTANT = {
  alex: ALEX_IDENTITY,
  bk: BK_IDENTITY,
  kushal: KUSHAL_IDENTITY,
  namrata: NAMRATA_IDENTITY,
  suman: SUMAN_IDENTITY,
  william: WILLIAM_IDENTITY,
  nisha: NISHA_IDENTITY,
  doug: DOUG_IDENTITY,
} as const satisfies Readonly<Record<ZarvisAssistantKey, ZarvisIdentity>>;

function configuredIdentity(assistant: string): ZarvisIdentity {
  if (!Object.prototype.hasOwnProperty.call(IDENTITY_BY_ASSISTANT, assistant)) {
    throw new Error(`Unknown configured Zarvis assistant: ${assistant}`);
  }
  return IDENTITY_BY_ASSISTANT[assistant as ZarvisAssistantKey];
}

if (workspaceIdentities.version !== 1) {
  throw new Error("Unsupported Zarvis workspace identity configuration");
}
const DEFAULT_IDENTITY = configuredIdentity(workspaceIdentities.default_assistant);
const IDENTITY_BY_WORKSPACE: Readonly<Record<string, ZarvisIdentity>> = Object.fromEntries(
  Object.entries(workspaceIdentities.workspaces).map(([scopeKey, { assistant }]) => [
    scopeKey, configuredIdentity(assistant),
  ]),
);

/** Configurable towers and wake-word defaults follow the shared workspace assignments. */
export interface ZarvisTowerIdentity {
  scopeKey: string;
  label: string;
  firstName: string;
}

export const ZARVIS_TOWER_IDENTITIES: readonly ZarvisTowerIdentity[] = Object.entries(
  workspaceIdentities.workspaces,
).map(([scopeKey, { label, assistant }]) => ({
  scopeKey,
  label,
  // The display name IS the first name today; splitting keeps this correct if names move to
  // "First L" (proposed 4 Sep), because the wake word is the first name either way.
  firstName: configuredIdentity(assistant).displayName.trim().split(/\s+/)[0],
}));

/**
 * Normalize only superficial label differences. The resulting key is still
 * exact-matched so a client or engagement containing a tower name cannot pick
 * a tower identity accidentally.
 */
export function normalizeZarvisWorkspaceLabel(label: string): string {
  return label
    .normalize("NFKC")
    .trim()
    .toLocaleLowerCase("en-US")
    .replace(/\band\b/gu, "&")
    .replace(/[^a-z0-9]+/gu, "");
}

/** Resolve the presentation identity without changing Zarvis's technical/API identity. */
export function resolveZarvisIdentity(
  workspaceLabel: string | null | undefined,
  global = false,
): ZarvisIdentity {
  if (global || !workspaceLabel) return DEFAULT_IDENTITY;
  return IDENTITY_BY_WORKSPACE[normalizeZarvisWorkspaceLabel(workspaceLabel)] ?? DEFAULT_IDENTITY;
}
